import {
  EarningsInsightFilterResponse,
  EarningsInsightsFilterRequest,
  EarningsInsightType,
  earningsInsights,
} from '@api';
import { getTimeTillEndOfDayMs } from '@helpers';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useKgInsightFilter } from '@terminal/react-query-hooks/insights/useKgInsightFilter';
import {
  mapEarningsInsightsFilterRequestToKgFilterRequest,
  mapKgInsightToEarningsInsight,
} from '@terminal/services/insights/utils/catalyst-insight-to-kg-mapper';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { wretchRequest } from '@terminal/utils/api-fetch/apiFetch';
import { useMemo } from 'react';

import { deduplicateEarningsInsights } from './useEarningsInsightsFilter.utils';

const QUERY_KEY_EARNINGS_INSIGHTS_FILTER = 'QUERY_KEY_EARNINGS_INSIGHTS_FILTER';

const MAX_PAGE_SIZE = 100;

/** TODO: handle API pagination **/
/**
 * @description Fetches only the first page of earnings insights — at most `pageSize`
 * items (100 by default). `fetchNextPage` is never called, so any insight
 * beyond that limit is not returned. Callers needing the complete result
 * set must paginate themselves.
 */
export const useEarningsInsightsFilter = ({
  body = {},
  isEnabled = true,
  earningsTypes,
  pageSize = MAX_PAGE_SIZE,
}: {
  body?: EarningsInsightsFilterRequest;
  isEnabled?: boolean;
  earningsTypes?: EarningsInsightType[];
  pageSize?: number;
}) => {
  const isCatalystGenerationEnabled = useFlags(state =>
    state.getFeatureFlagState('catalyst_generation')
  );
  const shouldFetchKg = isEnabled && isCatalystGenerationEnabled;

  const {
    data,
    isLoading: isEarningsLoading,
    isFetching: isEarningsFetching,
    isError: isEarningsError,
    error: earningsError,
  } = useInfiniteQuery({
    queryKey: [QUERY_KEY_EARNINGS_INSIGHTS_FILTER, body, pageSize],
    queryFn: ({ signal, pageParam }) => {
      const { path, schema } = earningsInsights.postEarningsInsightFilter;
      const requestBody: EarningsInsightsFilterRequest = {
        ...body,
        page: pageParam,
        page_size: pageSize,
      };
      return wretchRequest<EarningsInsightFilterResponse>(path, {
        method: 'post',
        body: requestBody,
        schema,
        signal,
      });
    },
    initialPageParam: 1,
    staleTime: getTimeTillEndOfDayMs(),
    enabled: isEnabled,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.length === pageSize ? lastPageParam + 1 : undefined,
  });

  const earningsData = data?.pages.flat() ?? [];

  const {
    data: kgData,
    isLoading: isKgLoading,
    isFetching: isKgFetching,
    isError: isKgError,
    error: kgError,
  } = useKgInsightFilter(
    mapEarningsInsightsFilterRequestToKgFilterRequest(body),
    shouldFetchKg
  );

  const earningsInsightsData = useMemo(() => {
    const kgInsights = (kgData ?? [])
      .filter(insight => !!insight.subject.ref?.id)
      .map(mapKgInsightToEarningsInsight);

    const merged = deduplicateEarningsInsights([
      ...kgInsights,
      ...earningsData,
    ]);

    return merged
      .filter(i => !earningsTypes || earningsTypes.includes(i.type))
      .map(insight => {
        return {
          ...insight,
          date: insight.created_at,
        };
      });
  }, [earningsData, kgData, earningsTypes]);

  return {
    data: earningsInsightsData,
    isLoading: isEarningsLoading || (shouldFetchKg && isKgLoading),
    isFetching: isEarningsFetching || (shouldFetchKg && isKgFetching),
    isError: isEarningsError && (!shouldFetchKg || isKgError),
    error: earningsError ?? (shouldFetchKg ? kgError : null),
  };
};
